import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, ClipboardCheck, Compass, History, Mail } from "lucide-react";

const CONTACT_EMAIL = "Mike@commongroundinstitute.net";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/assessment/history");
        if (alive) setItems(data.assessments || []);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-clinical" data-testid="dashboard-page">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Hero */}
        <section className="max-w-3xl animate-fade-in-up">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">
            Capacity for Conflict Management Scale
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 leading-tight">
            Quiz: How Well Do You Manage Conflict?
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Welcome{user?.name ? `, ${user.name}` : ""}. This 32-item self-assessment measures three
            capacities — empathic engagement, calm expression of needs, and composure under pressure —
            plus a brief supplemental measure of conflict engagement, and maps your style on a 2-D graph.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12">
              <Link to="/assessment" data-testid="start-assessment-button">
                Start the assessment <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* What you'll learn */}
        <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-testid="what-youll-learn">
          {[
            {
              icon: ClipboardCheck,
              title: "Three subscale scores",
              body: "Empathic Engagement, Calm Expression of Needs, and Composure Under Pressure — each on a 0–100 scale with interpretive feedback.",
            },
            {
              icon: Compass,
              title: "Conflict style quadrant",
              body: "Your placement on a 2-D graph: Self-Focused ↔ Empathic Listening, and Blaming/Criticism ↔ Calm Needs-Based Expression.",
            },
            {
              icon: History,
              title: "Strengths & growth plan",
              body: "Personalised strengths, challenges, and concrete recommendations you can practise in your next conversation.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <Card key={title} className="border-slate-200 bg-white">
              <CardContent className="p-6 sm:p-8">
                <div className="h-10 w-10 rounded-lg bg-accent text-primary flex items-center justify-center mb-5">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* History */}
        <section className="mt-16" data-testid="history-section">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">Your past assessments</h2>
            <span className="text-sm text-slate-500">{items.length} total</span>
          </div>

          {loading ? (
            <Card className="border-slate-200"><CardContent className="p-8 text-slate-500">Loading…</CardContent></Card>
          ) : items.length === 0 ? (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="p-10 text-center">
                <p className="text-slate-600">You haven&apos;t taken the assessment yet.</p>
                <Button asChild className="mt-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Link to="/assessment" data-testid="empty-start-button">Take it now</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((it) => (
                <Link
                  key={it.id}
                  to={`/results/${it.id}`}
                  className="group bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`history-item-${it.id}`}
                >
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                    {formatDate(it.created_at)}
                  </p>
                  <p className="mt-3 font-heading text-2xl font-semibold text-slate-900">
                    {it.overall_percent}<span className="text-base text-slate-500">/100</span>
                  </p>
                  <p className="text-sm text-slate-600">{it.overall_level}</p>
                  <p className="mt-4 text-sm font-medium text-primary group-hover:underline">
                    {it.quadrant_name} →
                  </p>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Contact */}
        <section className="mt-20" data-testid="contact-section">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 sm:p-12">
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">Reach out</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight">
                  Questions? Comments? Feedback? Contact us now.
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  We&apos;d love to hear from you. Mike will personally read every message — share what&apos;s working,
                  what isn&apos;t, or ask anything about your results and the Common Ground Institute.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:items-end">
                <Button
                  asChild
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 w-full sm:w-auto"
                >
                  <a
                    href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Capacity for Conflict Management Scale — message")}`}
                    data-testid="contact-email-button"
                  >
                    <Mail className="h-4 w-4" /> Email Mike
                  </a>
                </Button>
                <p className="text-xs text-slate-500 sm:text-right">
                  Or write directly:{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-primary font-medium hover:underline"
                    data-testid="contact-email-link"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
